import { Type } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { DialogBaseViewComponent } from '../../views/dialog-base-view.component';
import { Enum, FieldOf } from '../base.definitions';
import { FormSaveState } from './form-extension.definitions';
import { Validators } from './form-validators.definitions';

export enum FormContainerKind {
    Default = "Default",
    None = "None",
    Expandable = "Expandable",
    TextArea = "TextArea",
    PayPal = "PayPal",
    PayPalStyleMatch = "PayPalStyleMatch",
    Froala = "Froala",
    Checkbox = "Checkbox",
    Radio = "Radio",
    Text = "Text",
    Date = "Date",
    Select = "Select",
    Chips = "Chips",
    SegmentedButton = "SegmentedButton",
    SubForm = "SubForm",
    Star = "Star",
    Switch = "Switch",
    Legacy = "Legacy",
}

export enum TextKind {
    Default = "Default",
    CodeBlock = "CodeBlock"
}

export enum Orientation {
    Horizontal = "Horizontal",
    Vertical = "Vertical"
}

export enum RangeLabelValue {
    Start = 'start',
    End = 'end'
}

export enum FormContainerSize {
    Small = "Small",
    Medium = "Medium",
    Large = "Large"
}

export enum FormFieldLayout {
    Full = 'sb-form-field-full',
    Half = 'sb-form-field-half',
    ExclusiveHalf = 'sb-form-field-exclusive-half'
}

export type FormType<T> = FormGroup<{ [K in keyof T] : T[K] extends FormProperty<infer U> ? FormControl<U>
                                                     : T[K] extends (FormProperty<infer U>)[] ? FormArray<FormControl<U>>
                                                     : T[K] extends {}[] ? FormArray<FormType<T[K][number]>>
                                                     : T[K] extends {} ? FormType<T[K]>
                                                     : never }> & { Type: Type<T> };

export type BaseFormType<T> = Omit<FormType<T>, 'AddControl'>;   // Allow inheriting forms (AddControl causes problems extending FormType)

export class FormBase {
    private static _currentForm: FormGroup | null = null;
    private static _ignoreCreatedForms: boolean = false;

    static Create<T extends FormBase, U extends DialogBaseViewComponent>(this: Type<T>, setCurrentForm?: boolean, saveMethod?: () => void, unsavedChangesDialog?: Type<U>): FormType<T> {
        const form = FormBase.ConvertGroup(new this()) as FormType<T>;
        form.Type = this;

        // Below is ideal for later. For now, forms must explicitly opt in to form protection
        // setCurrentForm, if passed in, overrides ignoreCreatedForms flag
        //if (setCurrentForm || (setCurrentForm === undefined && !FormBase._ignoreCreatedForms)) 
        if (setCurrentForm) {
            FormBase._currentForm = form;

            if (saveMethod) {
                form._saveSubject = new Subject();
                form._saveMethod = () => {
                    form._saveState = FormSaveState.Saving;
                    saveMethod();
                }
            }

            form._unsavedChangesDialog = unsavedChangesDialog;
        }

        form.SetDefaultValueToCurrent();
        return form;
    }

    private static ConvertGroup<T extends object>(group: T): FormGroup {
        const object = {} as any;

        (Object.keys(group) as (FieldOf<T>)[]).forEach(key => {
            let value = group[key];

            if (value instanceof Array) {
                object[key] = this.ConvertArray(value, key);
            } else if (value instanceof FormProperty) {
                object[key] = this.ConvertItem(value);
            } else if (value && typeof value == 'object') {
                object[key] = this.ConvertGroup(value);
            } else {
                throw Error(`Incorrect form definition for field ${key}`);
            }
        });

        return new FormGroup(object);
    }

    private static ConvertArray<T>(array: FormProperty<T>[], key: string): FormArray<FormControl<T>> {
        if (array.length != 1) {
            throw Error(`Incorrect form array definition for field ${key} - form arrays are created empty, `
                      + 'but must be defined using a single prototype item (e.g. [Optional(boolean)])');
        }

        const formArray = new FormArray<FormControl<T>>([]);
        formArray._isRequired = array[0].Required;
        formArray.defaultControlValue = array[0].Default;
        formArray.generator = () => {
            let value = array[0];

            if (value instanceof Array) {
                return this.ConvertArray(value, '');
            } else if (value instanceof FormProperty) {
                return this.ConvertItem(value);
            } else if (value && typeof value == 'object') {
                return this.ConvertGroup(value);
            } else {
                throw Error(`Incorrect form definition for field`);
            }
        };

        return formArray;
    }

    private static ConvertItem<T>(item: FormProperty<T>): FormControl<T> {
        let formControl: FormControl<T>;

        if (item.Required) {
            const validator = typeof item.Default === 'boolean' ? Validators.requiredTrue : Validators.required;
            formControl = new FormControl<T>(item.Default, {nonNullable: true});
            formControl.AddValidators(validator);
        } else {
            formControl = new FormControl<T>(item.Default, {nonNullable: true});
            formControl.isRequired = false;
        }

        formControl.RegisterForErrorCount();
        return formControl;
    }

    static get currentForm(): FormGroup<any> | null {
        return FormBase._currentForm;
    }

    static set currentForm(value: FormGroup<any> | null) {
        FormBase._currentForm = value;
    }

    static set ignoreCreatedForms(value: boolean) {
        FormBase._ignoreCreatedForms = value;
    }
}

export class FormProperty<T> {
    Default!: T;
    Required!: boolean

    constructor(initializer: FormProperty<T>) {
        Object.assign(this, initializer);
    }
}

function FormItem<T>(x: Type<T>, y: Type<T>): FormProperty<[T, T] | null>;   // Generalize? For now, only used for date range
function FormItem(x: 'boolean'): FormProperty<boolean>;
function FormItem(x: 'number'): FormProperty<number | null>;
function FormItem(x: 'string'): FormProperty<string>;
function FormItem(x: ['boolean']): FormProperty<boolean[]>;
function FormItem(x: ['number']): FormProperty<number[]>;
function FormItem(x: ['string']): FormProperty<string[]>;
function FormItem<T>(x: Type<T>): FormProperty<T | null>;
function FormItem<T>(x: Enum<T>): FormProperty<T[keyof T]>;
function FormItem<T>(x: [Type<T>]): FormProperty<T[]>;
function FormItem<T>(x: [Enum<T>]): FormProperty<T[keyof T][]>;
function FormItem(this: { required: boolean }, x: any, y?: any): FormProperty<any> {
    if (y || typeof x === 'function' || x === 'number') {  // Class
        return new FormProperty<any>({ Default: null, Required: this.required });
    } else if (Array.isArray(x)) {
        return new FormProperty<any[]>({ Default: [], Required: this.required });
    } else if (typeof x === 'object') {  // Enum
        return new FormProperty<any>({ Default: Object.values(x)[0] ?? null, Required: this.required });
    } else if (x === 'boolean') {
        return new FormProperty<boolean>({ Default: false, Required: this.required });
    } else if (x === 'string') {
        return new FormProperty<string>({ Default: '', Required: this.required });
    }

    throw Error('Unknown form definition');
}

export const Optional = FormItem.bind({ required: false });
export const Required = FormItem.bind({ required: true });
